<script lang="ts" setup>
import { ref, Ref, computed, watch, inject, nextTick, onBeforeMount, onBeforeUnmount, getCurrentInstance } from 'vue';
import { useEvents } from '@/plugins/events';
import properties from '@/components/forms/properties';
import { RootEntry } from '@/components/builder/form/entries/RootEntry';
import { instanceOfEmailEntry, EmailType, EmailEntry } from '.';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';
import { useDynamicTrigger, useDynamicTriggerChannelId } from '@/components/forms/helpers/DynamicTriggerHelper';
import { TriggerMoments, TriggerModes } from '@/components/builder/form/traits/HasTriggers';
import { FieldTrigger } from '@/components/forms/properties';
import { registerComponent, unRegisterComponent } from '@/store/triggers';
import { createTriggerComponentCtx } from '@/components/builder/form/traits/TriggerContext';

defineOptions({
    name: 'email-blueprint',
    components: {
        ...properties
    }
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null
});

const getDynamicTriggerChannelId = useDynamicTriggerChannelId(() => props.form.blueprint.id);

const level = inject<number>('level', 0);
const { $events } = useEvents();
const blueprint = computed(() => props.blueprint);
const entryData = ref(new EmailEntry()) as Ref<EmailEntry>;

const triggerComponentCtx = createTriggerComponentCtx(
    {

    }
);

const loadControlData = () =>
{
    return props.form.document.initEntry(props.blueprint, entryData.value, instanceOfEmailEntry, props.index);
};

const setControlData = () =>
{
    entryData.value = loadControlData();
    initEmails();
};

entryData.value = loadControlData();

onBeforeMount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOn(setControlData);
        else
            useControlReload($events, props.blueprint.name, level).eventOn(setControlData);

        registerComponent(props.form.blueprint.id, props.blueprint.name, (getCurrentInstance()?.props), triggerComponentCtx);
    }
});

onBeforeUnmount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOff();
        else
            useControlReload($events, props.blueprint.name, level).eventOff();

        unRegisterComponent(props.form.blueprint.id, props.blueprint.name);
    }
});

const readonly = computed(() => props.form.expressions.readonly(props.blueprint, false, props.index));
const required = computed(() => props.form.expressions.required(props.blueprint, props.index));
const placeholder = computed(() => props.form.localization.translate(props.blueprint.placeholder));

// Multi-email support
const emails = ref<string[]>(['']);
const emailKeys = ref<string[]>([crypto.randomUUID()]);
const inputRefs = ref<HTMLInputElement[]>([]);

const initEmails = () =>
{
    const data = entryData.value.data;
    const resolved = (data && readonly.value && props.blueprint.defaultValue)
        ? props.form.expressions.executeExpression(props.blueprint.defaultValue, props.index) as string
        : data;

    emails.value = resolved
        ? resolved.split(';').map(e => e.trim()).filter(Boolean)
        : [''];

    if (emails.value.length === 0) emails.value = [''];
    emailKeys.value = emails.value.map(() => crypto.randomUUID());
};

initEmails();

// Sync emails UI state → entryData (empty fields allowed in UI, filtered on save)
watch(emails, (val) =>
{
    const filtered = val.filter(e => e.trim().length > 0);

    entryData.value.data = filtered.length > 0 ? filtered.join(';') : null;
    useDynamicTrigger($events, getDynamicTriggerChannelId()).emit(TriggerMoments.onChange, props.blueprint, props.parent?.type === 'table' ? props.index : null);
}, { deep: true });

const addEmail = () =>
{
    emails.value.push('');
    emailKeys.value.push(crypto.randomUUID());
};

const removeEmail = (idx: number) =>
{
    emails.value.splice(idx, 1);
    emailKeys.value.splice(idx, 1);
};

const handlePaste = (event: ClipboardEvent, idx: number) =>
{
    const pasted = event.clipboardData?.getData('text') ?? '';

    if (pasted.includes(';'))
    {
        event.preventDefault();

        const parts = pasted.split(';').map(e => e.trim()).filter(Boolean);

        emails.value.splice(idx, 1, ...parts);
        emailKeys.value.splice(idx, 1, ...parts.map(() => crypto.randomUUID()));
    }
};

const handleKeydown = async (event: KeyboardEvent, idx: number) =>
{
    if (event.key !== ';') return;

    event.preventDefault();

    const nextEmptyIdx = emails.value.findIndex((e, i) => i > idx && e.trim().length === 0);

    if (nextEmptyIdx !== -1)
    {
        await nextTick();
        inputRefs.value[nextEmptyIdx]?.focus();
    }
    else
    {
        emails.value.push('');
        emailKeys.value.push(crypto.randomUUID());
        await nextTick();
        inputRefs.value[emails.value.length - 1]?.focus();
    }
};
</script>

<template>
    <form-component-wrapper class="email-component" :form="form" :parent="parent" :blueprint="blueprint">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />
                <div v-for="(email, idx) in emails" :key="emailKeys[idx]" class="d-flex align-items-center mb-2">
                    <ideo-input-group :prepend="blueprint.affix.prepend" :append="blueprint.affix.append" class="flex-grow-1">
                        <input type="email" class="form-control" v-model="emails[idx]"
                               :ref="(el) => { if (el) inputRefs[idx] = el as HTMLInputElement }"
                               :id="idx === 0 ? blueprint.id : undefined"
                               :placeholder="placeholder" :readonly="readonly"
                               @paste="handlePaste($event, idx)"
                               @keydown="handleKeydown($event, idx)">
                    </ideo-input-group>
                    <button
                        v-if="emails.length > 1 && !readonly" @click="removeEmail(idx)"
                        class="btn btn-danger btn-sm ms-2" type="button"
                    >
                        <i class="fa-light fa-minus" />
                    </button>
                </div>
                <button
                    v-if="!readonly" @click="addEmail"
                    class="btn btn-primary btn-sm" type="button"
                >
                    <i class="fa-light fa-plus me-1" />{{ $t('[[[Dodaj email]]]') }}
                </button>
                <form-error-message :entry="entryData" name="value" />
                <form-error-message :entry="entryData" name="custom" />
                <form-help :form="form" :blueprint="blueprint" />
            </div>
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.name" />
            <IdeoTabs pills nav-class="nav-justified" content-class="mt-3 docs-container">
                <IdeoTab :title="$t('[[[Konfiguracja]]]')" active>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
                    </ideo-form-localize>
                    <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
                    <field-condition v-model="blueprint.defaultValue" :form="form" :label="$t('[[[Wartość domyślna]]]')" type="text" />
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.placeholder[locale]" :label="$t('[[[Tekst zastępczy]]]')" />
                    </ideo-form-localize>
                    <field-affix v-model="blueprint.affix" />
                    <ideo-form-localize v-slot="{ locale }">
                        <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
                    </ideo-form-localize>
                    <field-visible :form="form" :blueprint="blueprint" />
                    <field-readonly :form="form" :blueprint="blueprint" />
                    <field-required :form="form" :blueprint="blueprint" />
                    <field-error :form="form" :blueprint="blueprint" />
                </IdeoTab>
                <IdeoTab :title="$t('[[[Wyzwalacze]]]')">
                    <FieldTrigger v-model="blueprint.serverSideTriggers" :mode="TriggerModes.ServerSide" :blueprint="blueprint" />
                    <FieldTrigger v-model="blueprint.clientSideTriggers" :mode="TriggerModes.ClientSide" :blueprint="blueprint" />
                </IdeoTab>
            </IdeoTabs>
        </template>
    </form-component-wrapper>
</template>

<style lang="scss" scoped>
</style>
